'use client'

import { type FC, type ReactNode, useId } from 'react'
import { FormattedMessage } from 'react-intl'

import { type SellerPromotionV2 } from '@redteclab/api/clients/promotion-service'

import { Dialog, DialogContent, DialogTitle } from 'base-ui'

import { DynamicProductSellerPromotionDialogContent } from './DynamicProductSellerPromotionDialogContent'
import { useProductSellerPromotionDetails } from './useProductSellerPromotionDetails'

import styles from './ProductSellerPromotionDialog.module.css'

export const ProductSellerPromotionDialog: FC<{
  isOpen: boolean
  onClose: () => void
  sellerName: string
  sellerPromotion: SellerPromotionV2
  showLinkToPromotionPage?: boolean
}> = ({
  isOpen,
  onClose,
  sellerName,
  sellerPromotion,
  showLinkToPromotionPage = true,
}) => {
  const titleId = useId()
  const {
    discount,
    isFreeItems,
    isQuantityPromotion,
    isSameItemPromotion,
    minimumOrderValue,
    payableQuantity,
  } = useProductSellerPromotionDetails(sellerPromotion)

  let title: ReactNode = null

  if (isSameItemPromotion && isFreeItems) {
    title = (
      <FormattedMessage
        id="seller.promotion.dialog.quantityPromotion.title"
        values={{
          buyItems: sellerPromotion.conditionValue,
          payFor: payableQuantity,
        }}
      />
    )
  } else if (isQuantityPromotion && isFreeItems) {
    title = (
      <FormattedMessage
        id="seller.promotion.dialog.freeItems.title"
        values={{
          freeItems: sellerPromotion.discountValue,
          minimumOrderValue: sellerPromotion.conditionValue,
        }}
      />
    )
  } else if (isQuantityPromotion) {
    title = (
      <FormattedMessage
        id="seller.promotion.product.discount.minQuantity"
        values={{
          discount,
          minimumItemCount: sellerPromotion.conditionValue,
          sup: '',
        }}
      />
    )
  } else if (minimumOrderValue) {
    title = (
      <FormattedMessage
        id="seller.promotion.dialog.minimumOrderValue.title"
        values={{ discount, minimumOrderValue }}
      />
    )
  }

  return (
    <Dialog
      data-qa-id="seller-promotion-dialog"
      onClose={onClose}
      open={isOpen}
      slotProps={{
        dialog: { 'aria-labelledby': titleId, className: styles.dialog },
      }}
    >
      <DialogTitle
        className="items-center gap-2.5 self-stretch p-4"
        onClose={onClose}
        slotProps={{ heading: { id: titleId } }}
      >
        {title}
      </DialogTitle>
      <DialogContent className="self-stretch p-4">
        <DynamicProductSellerPromotionDialogContent
          sellerName={sellerName}
          sellerPromotion={sellerPromotion}
          showLinkToPromotionPage={showLinkToPromotionPage}
        />
      </DialogContent>
    </Dialog>
  )
}
